/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { ReactNode } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogProps,
  LinearProgress,
} from '@mui/material'
import { Button } from '..'

type Props = DialogProps & {
  children: ReactNode
  classes?: any
  cancelButton?: {
    text: string
    onClick: () => void
    disabled?: boolean
  }
  okayButton?: {
    text: string
    onClick: () => void
    disabled?: boolean
  }
  onClose: () => void
  submitting?: boolean
  open: boolean
  title?: ReactNode
  progressValue?: number
  footer?: ReactNode
}

export default function Modal(props: Props): JSX.Element {
  const {
    children,
    classes,
    cancelButton,
    okayButton,
    onClose,
    submitting,
    open,
    title,
    progressValue,
    footer,
    ...restOfProps
  } = props

  const showProgressBar = typeof progressValue !== 'undefined' && submitting

  return (
    <Dialog {...restOfProps} classes={classes} open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof children === 'string' ? <DialogContentText>{children}</DialogContentText> : children}
      </DialogContent>
      <div
        css={css`
          margin: 0 24px;
        `}
      >
        {footer}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          align-items: center;
        `}
      >
        {showProgressBar && (
          <LinearProgress
            css={(theme: any) => css`
              margin-left: 20px;
              border-radius: 5px;
              color: ${theme?.palette?.primary?.main || 'hsl(321, 53%, 31%)'};
              flex: 1 0 auto;

              & .MuiLinearProgress-bar {
                border-radius: 5px;
              }
            `}
            variant="determinate"
            value={progressValue}
          />
        )}
        <DialogActions>
          {cancelButton && !showProgressBar && (
            <Button variant="text" disabled={cancelButton.disabled || submitting} onClick={cancelButton.onClick}>
              {cancelButton.text}
            </Button>
          )}
          {okayButton && (
            <Button
              css={(theme: any) => css`
                &&.MuiButton-root .MuiCircularProgress-root {
                  color: ${theme?.palette?.primary?.main || 'hsl(321, 53%, 31%)'};
                }
              `}
              color="primary"
              variant="text"
              disabled={okayButton.disabled}
              onClick={okayButton.onClick}
              loading={submitting}
            >
              {okayButton.text}
            </Button>
          )}
        </DialogActions>
      </div>
    </Dialog>
  )
}
